import React, { useEffect, useRef, useState } from 'react';
import { EventBus, EVENTS } from '../../../shared/EventBus';
import { isThrottledFrameGap, type FreezeEventWire } from '../../../shared/diagnostics/FreezeEventTypes';
import { FREEZE_VISUAL_CONTROL_EVENT, type FreezeVisualControlDetail } from '../../../shared/diagnostics/FreezeVisualControl';
import { readFreezeToastEnabled } from './freezeToastPreference';
import { FONTS, ACCENT } from '../theme';

export const ConnectionStatusOverlay: React.FC = () => {
  const [reconnectState, setReconnectState] = useState<{
        isReconnecting: boolean;
        attempt: number;
        maxAttempts: number;
    } | null>(null);

  // Subtle, OPT-IN freeze tag — flashes briefly when the FreezeSentinel
  // trips, colored by the local client/server hint. Distinct from the
  // blocking reconnect modal below (a 150ms hitch shouldn't black out the screen).
  // Off by default since 2026-07-26 (dev.freezeCornerToast / ?freezeToast=1);
  // detection and durable DomainLog recording are unaffected.
  const [freeze, setFreeze] = useState<{ gapMs: number; sideHint: string } | null>(null);
  // Capture tooling can hide the visual and trigger a freeze in the same
  // frame. A ref makes that control visible to the event handler immediately.
  const freezeVisualsHiddenRef = useRef(false);

  useEffect(() => {
    const control = (event: Event) => {
      if (!(window as any).playwrightTest) return;
      const detail = (event as CustomEvent<FreezeVisualControlDetail>).detail;
      freezeVisualsHiddenRef.current = Boolean(detail?.hidden);
      if (detail?.clear) setFreeze(null);
    };
    window.addEventListener(FREEZE_VISUAL_CONTROL_EVENT, control);
    return () => window.removeEventListener(FREEZE_VISUAL_CONTROL_EVENT, control);
  }, []);

  useEffect(() => {
    const unsub1 = EventBus.getInstance().on(EVENTS.NETWORK_RECONNECTING, (data: any) => {
      setReconnectState({
        isReconnecting: true,
        attempt: data.attempt,
        maxAttempts: data.maxAttempts,
      });
    });

    const unsub2 = EventBus.getInstance().on(EVENTS.NETWORK_CONNECTION_RESTORED, () => {
      setReconnectState(null);
    });

    // A replacement room join (for example StrictMode/dev boot cleanup)
    // is a fresh connection rather than an SDK "reconnect", so it emits
    // net:connected but not network:connection_restored. Clear any modal
    // left by the superseded room's 4000 close once the new room is live.
    const unsubConnected = EventBus.getInstance().on(EVENTS.NET_CONNECTED, () => {
      setReconnectState(null);
    });

    const unsub3 = EventBus.getInstance().on(EVENTS.NETWORK_CONNECTION_FAILED, () => {
      setReconnectState(prev => prev ? { ...prev, isReconnecting: false } : null);
    });

    // Additional listen for total connection lost (intentional or kicks)
    const unsub4 = EventBus.getInstance().on('network:connection_lost', () => {
      // Don't show if we are actively trying to reconnect
      setReconnectState(prev => {
        if (prev && prev.isReconnecting) return prev;
        return { isReconnecting: false, attempt: 0, maxAttempts: 0 };
      });
    });

    return () => {
      unsub1();
      unsub2();
      unsubConnected();
      unsub3();
      unsub4();
    };
  }, []);

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | undefined;
    const unsub = EventBus.getInstance().on(EVENTS.SYSTEM_FREEZE, (e: FreezeEventWire) => {
      if (freezeVisualsHiddenRef.current) return;
      // Read per-event, not per-mount, so toggling takes effect on the
      // next freeze instead of requiring a remount or reload.
      if (!readFreezeToastEnabled()) return;
      // This tag says "you just froze". A gap the browser produced by
      // throttling an unfocused window is not that, and would flash the
      // word "freeze" every time DJ tabs back in. Same containment the
      // FreezeErrorOverlay already applies: presentation only — the event
      // is already recorded as `raf_throttled` in the DomainLog and is
      // listed, labelled, in the Freeze Log tab.
      if (isThrottledFrameGap(e)) return;
      // FreezeSentinel emits a FreezeEventWire {kind, stallMs, ...}. The tag
      // previously read e.gapMs/e.sideHint — fields that don't exist on the
      // wire — so it was pinned to "freeze 0ms · client" and never went red.
      // Map from the real fields: duration ← stallMs, side ← kind
      // (server_silence is the only server/network freeze class).
      setFreeze({
        gapMs: Math.round(e?.stallMs ?? 0),
        sideHint: e?.kind === 'server_silence' ? 'server_or_network' : 'client',
      });
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => setFreeze(null), 1800);
    });
    return () => {
      unsub();
      if (timer) clearTimeout(timer);
    };
  }, []);

  if (!reconnectState && !freeze) return null;

  return (
    <>
      {freeze && (
        <div style={{
          position: 'fixed' as const,
          bottom: 8,
          right: 8,
          zIndex: 9998,
          padding: '4px 10px',
          borderRadius: 6,
          fontFamily: 'monospace',
          fontSize: 12,
          color: '#fff',
          pointerEvents: 'none' as const,
          background: freeze.sideHint === 'server_or_network' ? 'rgba(180,40,40,0.92)' : 'rgba(190,140,30,0.92)',
          border: '1px solid rgba(255,255,255,0.25)',
        }}>
          {`⏱ freeze ${freeze.gapMs}ms · ${freeze.sideHint === 'server_or_network' ? 'server?' : 'client'}`}
        </div>
      )}
      {reconnectState && (
        <div style={{
          position: 'fixed' as const,
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.85)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999,
          pointerEvents: 'all'
        }}>
          <div className="tome-tokens" style={{
            color: '#fff',
            textAlign: 'center',
            padding: '40px',
            border: `1px solid ${ACCENT.brass}`,
            backgroundColor: 'var(--tome-hud-ground-hi, #111)',
            borderRadius: '8px',
            boxShadow: '0 0 20px rgba(0,0,0,0.5)'
          }}>
            {reconnectState.isReconnecting ? (
              <>
                <h2 style={{ fontFamily: FONTS.display, color: '#FFD700', marginBottom: '10px' }}>🔄 Reconnecting...</h2>
                <p style={{ fontSize: '16px', color: '#AAA' }}>
                            Attempt {reconnectState.attempt} of {reconnectState.maxAttempts}
                </p>
              </>
            ) : (
              <>
                <h2 style={{ fontFamily: FONTS.display, color: '#FF4444', marginBottom: '10px' }}>❌ Connection Lost</h2>
                <p style={{ fontSize: '14px', color: '#888', marginBottom: '20px' }}>
                            Unable to communicate with the server.
                </p>
                <button
                  onClick={() => window.location.reload()}
                  style={{
                    padding: '10px 20px',
                    backgroundColor: 'var(--tome-gilt, #333)',
                    color: 'var(--tome-ink, #FFF)',
                    border: '1px solid var(--tome-gilt-bright, #555)',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    textTransform: 'uppercase'
                  }}
                >
                            Reload Game
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
};
